/**
 * Shaping helpers for the Unallocated Summary tree the index endpoint returns:
 *
 *   { departments: { [id]: { ..., costcenters: { [id]: { ..., projects: [] } }, projects: [] } },
 *     grand: { total, projects_total, unallocated_percent } }
 *
 * Filtering runs here rather than on the server: the whole payload is already in
 * the browser, so a round trip per threshold change would put back the latency the
 * report just lost. The roll-up rules mirror UnallocatedSummaryService so the
 * filtered screen and a filtered export agree line for line.
 */

const PRECISION = 2;

/**
 * Colour bands for the unallocated percentage. Indicative and awaiting final
 * client confirmation — the whole scheme is these rows.
 *
 * The leading band catches over-allocated projects: a negative percentage is worse
 * than 100%, not better than 10%, and without it a project over its budget gets a
 * green badge sitting next to a parenthesised negative balance.
 */
export const PERCENT_BANDS = [
    { upTo: 10, variant: 'success' },
    { upTo: 75, variant: 'warning' },
    { upTo: Infinity, variant: 'danger' },
];

/**
 * Over-allocation is its own state rather than a small percentage, and the balance is
 * what decides it. A project 1.00 over a 5,000,000 budget rounds to -0.00%, so banding
 * on the percentage alone would badge it green right beside a parenthesised negative —
 * and a fully-allocated project sits at exactly 0 and has to stay green.
 */
export const percentBandVariant = (percent, balance = 0) => {
    if (balance < 0 || percent < 0) {
        return 'danger';
    }

    // Every comparison against NaN is false, so find() would return undefined and
    // reading .variant off it would throw while rendering — taking out every row in the
    // table, not just this badge. The server only ever sends null or a finite float, so
    // this should be unreachable; the blast radius is why it is guarded anyway.
    return PERCENT_BANDS.find((band) => percent <= band.upTo)?.variant ?? 'neutral';
};

const round = (value) => Math.round(value * 10 ** PRECISION) / 10 ** PRECISION;

/** Null rather than zero when there is nothing to divide by — see the service. */
const percent = (amount, of) => (of === 0 ? null : round((amount / of) * 100));

const sum = (rows, key) => round(rows.reduce((carry, row) => carry + row[key], 0));

const byServerOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

/**
 * Departments and cost centres arrive keyed by id, and JavaScript iterates
 * integer-like object keys in ascending numeric order however the server wrote them,
 * so an order has to be applied here. It is the server's: getTree() sorts by name and
 * stamps each group with its position, and this replays it. Sorting by name again
 * here would be a second comparator that disagrees with the export's the moment a
 * name is not plain ASCII. Projects are already lists, so their order survives intact.
 */
export const departmentList = (tree) =>
    Object.values(tree?.departments ?? [])
        .map((department) => ({
            ...department,
            costcenters: Object.values(department.costcenters ?? []).sort(byServerOrder),
            projects: [...(department.projects ?? [])],
        }))
        .sort(byServerOrder);

export const countProjects = (departments) =>
    departments.reduce(
        (carry, department) => carry + department.projects.length + department.costcenters.reduce((n, costcenter) => n + costcenter.projects.length, 0),
        0,
    );

export const hasActiveFilter = (filter) => filter.minPercent !== '' || filter.minBalance !== '';

/**
 * The tree with project rows the thresholds exclude removed, every subtotal
 * recalculated over what survives, and any group left with nothing dropped.
 * Thresholds are lower bounds combined as AND; an empty filter is a no-op.
 */
export const filterDepartments = (departments, filter) => {
    const minPercent = filter.minPercent === '' ? null : Number(filter.minPercent);
    const minBalance = filter.minBalance === '' ? null : Number(filter.minBalance);

    if (minPercent === null && minBalance === null) {
        return departments;
    }

    const keep = (project) => {
        // A project with no value has no percentage, so it can never clear a
        // percentage threshold.
        if (minPercent !== null && (project.unallocated_percent === null || project.unallocated_percent < minPercent)) {
            return false;
        }

        return minBalance === null || project.total >= minBalance;
    };

    return departments
        .map((department) => {
            const costcenters = department.costcenters
                .map((costcenter) => ({ ...costcenter, projects: costcenter.projects.filter(keep) }))
                .filter((costcenter) => costcenter.projects.length > 0)
                .map((costcenter) => withTotals(costcenter));

            const projects = department.projects.filter(keep);
            const rows = [...projects, ...costcenters.flatMap((costcenter) => costcenter.projects)];

            return withTotals({ ...department, costcenters, projects }, rows);
        })
        .filter((department) => department.projects.length > 0 || department.costcenters.length > 0);
};

const withTotals = (group, rows = null) => {
    const source = rows ?? group.projects;
    const total = sum(source, 'total');
    const projectsTotal = sum(source, 'project_total');

    return { ...group, total, projects_total: projectsTotal, unallocated_percent: percent(total, projectsTotal) };
};

export const grandTotals = (departments) => {
    const total = sum(departments, 'total');
    const projectsTotal = sum(departments, 'projects_total');

    return { total, projects_total: projectsTotal, unallocated_percent: percent(total, projectsTotal) };
};
